import { useMutation } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button } from '@bb/ui';
import { ApiError, type MergePreview } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

function Summary({ preview }: { preview: MergePreview }) {
  return (
    <section aria-label="Merge preview" className="panel">
      <h2>
        {preview.source.externalId} → {preview.target.externalId}
      </h2>
      <dl className="facts">
        <dt>Moves to the target</dt>
        <dd>{Object.entries(preview.moves).length ? Object.entries(preview.moves).map(([table, n]) => `${table}: ${n}`).join(', ') : 'nothing'}</dd>
        <dt>Target keeps its value</dt>
        <dd>{preview.attributeConflicts.join(', ') || '—'}</dd>
        <dt>Filled from the source</dt>
        <dd>{preview.attributesFilled.join(', ') || '—'}</dd>
        <dt>Subscriptions that get stricter</dt>
        <dd>{preview.subscriptionChanges.length ? preview.subscriptionChanges.map((c) => `${c.name}: ${c.from ?? 'none'} → ${c.to}`).join('; ') : '—'}</dd>
        <dt>Journeys that end</dt>
        <dd>{preview.journeysEnded}</dd>
      </dl>
      <p className="hint">Past message facts and reports stay with the source; its external ID keeps working and writes to the target.</p>
    </section>
  );
}

/**
 * Audience → Merge users (BB-159): two identified profiles become one. A preview (dry run) first; the merge itself
 * needs profiles.merge, a reason and a confirmation, and runs in the background.
 */
export function MergeProfilesPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/audience/merge' });
  const { api } = useRouteContext({ from: '/env/$environmentId/audience/merge' });
  const permissions = usePermissions();
  const [source, setSource] = useState('');
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const body = (dryRun: boolean) => ({ source: { externalId: source.trim() }, target: { externalId: target.trim() }, reason: reason.trim(), dryRun });
  const preview = useMutation({ mutationFn: () => api.mergeProfiles(environmentId, body(true)) });
  const merge = useMutation({ mutationFn: () => api.mergeProfiles(environmentId, body(false)) });
  if (!permissions.has('profiles.merge')) return <p className="hint">Merging users needs the profiles.merge permission.</p>;
  const ready = source.trim() && target.trim() && reason.trim();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setConfirmed(false);
    merge.reset();
    if (ready) preview.mutate();
  };
  return (
    <section>
      <h1>Merge users</h1>
      <p className="hint">For one person with two accounts (two external IDs). Anonymous profiles merge on their own when the user signs in.</p>
      <form className="stack-form" aria-label="Merge users" onSubmit={submit}>
        <label>
          Merge (source external ID)
          <input value={source} onChange={(e) => setSource(e.target.value)} />
        </label>
        <label>
          Into (target external ID)
          <input value={target} onChange={(e) => setTarget(e.target.value)} />
        </label>
        <label>
          Reason
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Duplicate account, ticket 512" />
        </label>
        <Button type="submit" disabled={!ready || preview.isPending}>
          Preview
        </Button>
      </form>
      {preview.isError && <p role="alert">{errorText(preview.error)}</p>}
      {preview.data && (
        <>
          <Summary preview={preview.data} />
          {merge.data ? (
            <p role="status">Merge started: the data moves in the background ({merge.data.mergeId}).</p>
          ) : (
            <section aria-label="Confirm merge" className="panel">
              <label>
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} /> I understand a merge cannot be undone
              </label>
              <Button variant="danger" onClick={() => merge.mutate()} disabled={!confirmed || merge.isPending}>
                Merge {preview.data.source.externalId} into {preview.data.target.externalId}
              </Button>
              {merge.isError && <p role="alert">{errorText(merge.error)}</p>}
            </section>
          )}
        </>
      )}
    </section>
  );
}
